import { computed, defineComponent, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { ActivityMemoAction, ActivityMemoGetter } from '@/store/activityMemo/activityMemo.module';
import { ActivityMemoResultAttachmentSchema } from '@/store/activityMemo/activityMemo.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'ActivityAttachmentUploadComponent',
  components: {
    BaseModalComponent,
  },
  props: {
    activityId: {
      type: Number,
    },
    provinceId: {
      type: Number,
    },
    activityMemoId: {
      type: Number,
    }
  },
  emits: [
    'hidden'
  ],
  setup: (props, context) => {
    const store = useStore();
    const activityAttachmentUploadForm = ref<InstanceType<typeof BaseModalComponent>>();
    const fileDialog = ref<HTMLInputElement>();

    // form
    interface Form {
      activityMemoId?: number;
      name: string;
      file: string;
    }
    const formData = reactive<Form>({
      activityMemoId: props.activityMemoId,
      name: '',
      file: '',
    });
    const validator = computed(() => ActivityMemoResultAttachmentSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      name: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'name')),
      file: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'file')),
    });
    const isValid = computed(() => !errors.name && !errors.file);
    const isLoading = computed(() => store.getters[ActivityMemoGetter.STATUS] === 'init' || store.getters[ActivityMemoGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show
    const show = () => {
      Promise.resolve(formData.activityMemoId = props.activityMemoId)
        .then(() => activityAttachmentUploadForm.value?.show());
    };

    // hide
    const hide = () => {
      activityAttachmentUploadForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.name = '';
        formData.file = '';
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        const files = fileDialog.value?.files as FileList;
        store.dispatch(ActivityMemoAction.ADD_ATTACHMENTS, {
          activityId: props.activityId,
          provinceId: props.provinceId,
          id: props.activityMemoId,
          attachmentTypeId: 1,
          names: [formData.name],
          attachments: files,
        }).then(() => hide());
      }
    };

    const fileDialogChange = () => {
      const files = fileDialog.value?.files as FileList;
      const file = files.item(0) as File;
      formData.file = file.name;
      unsubmit();
    };

    const handleHidden = () => {
      context.emit('hidden');
    };

    return {
      formData,
      activityAttachmentUploadForm,
      fileDialog,
      isLoading,
      errors,
      submited,
      show,
      hide,
      submitClick,
      unsubmit,
      clearForm,
      fileDialogChange,
      handleHidden,
    };
  }
});
